import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Stack,
  Paper,
  Divider,
  LinearProgress,
  TextField,
  MenuItem,
  Menu,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Gavel as GavelIcon,
  Edit as EditIcon,
  Replay as ReplayIcon,
  ContentCopy as ContentCopyIcon,
  MoreVert as MoreVertIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
} from '@mui/icons-material';

import SimpleLegalEditor from '../../components/editor/SimpleLegalEditor';
import JurisprudencePanel from '../../components/editor/JurisprudencePanel';
import IntegratedJurisprudencePanel from '../../components/editor/IntegratedJurisprudencePanel';
import ReviewChecklist, { loadReviewData, type ReviewData } from '../../components/documents/ReviewChecklist';
import api from '../../services/api';
import { takePendingCitation, citationToHtml, type PendingCitation } from '../../utils/pendingCitation';
import '../../components/editor/AdvancedLegalEditor.css';
import { FeedbackWidget } from '../../components/feedback';
import { trackEvent } from '../../services/documentFlowService';

interface Document {
  id: string;
  title: string;
  content: string;
  document_type?: string;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, any>;
}

const NewDocumentEditorPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [document, setDocument] = useState<Document | null>(null);
  const [content, setContent] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<any[]>([]); // Removed LegalTemplate[]
  const [infoDialog, setInfoDialog] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 'idle' | 'saving' | 'saved' | 'error' — estado visível do autosave
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  // refs para salvar ao sair sem depender de re-render
  const contentRef = useRef('');
  const hasUnsavedRef = useRef(false);
  const [validationResults, setValidationResults] = useState<{ isValid: boolean; errors: string[] }>({
    isValid: true,
    errors: []
  });
  const [showJurisprudencePanel, setShowJurisprudencePanel] = useState(false);
  const [documentData, setDocumentData] = useState<any>(null);
  const [isFromWizard, setIsFromWizard] = useState(false);
  const [showReviewChecklist, setShowReviewChecklist] = useState(false);
  const [reviewLayoutMode, setReviewLayoutMode] = useState<'side' | 'top'>('side');
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  const [loadingStep, setLoadingStep] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false); // Prevenir múltiplas chamadas
  const generationRef = useRef(false); // Ref para controlar execução única
  const processedStateRef = useRef<string | null>(null); // Ref para controlar state processado
  const reviewShownRef = useRef(false); // Evita mostrar o popup de revisão mais de uma vez por sessão
  const [isEditingTitle, setIsEditingTitle] = useState(false); // Controle de edição de título
  const [tempTitle, setTempTitle] = useState(''); // Título temporário durante edição
  const [downloadFormatDialog, setDownloadFormatDialog] = useState(false); // Dialog para escolher formato
  const [showTextFeedback, setShowTextFeedback] = useState(false); // Feedback de texto selecionado
  const [feedbackText, setFeedbackText] = useState(''); // Texto selecionado para feedback
  const [feedbackContext, setFeedbackContext] = useState(''); // Contexto do texto
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [regenerateDialog, setRegenerateDialog] = useState(false);
  const [reuseContextDialog, setReuseContextDialog] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [docContext, setDocContext] = useState<any>(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [citationInserted, setCitationInserted] = useState(false);
  // A citação é removida do sessionStorage na primeira leitura; a ref garante que um
  // segundo loadDocument (remontagem) ainda a aplique.
  const pendingCitationRef = useRef<PendingCitation | null>(null);

  const handleLoadDocContext = async () => {
    if (!documentId || documentId === 'new') return;
    setLoadingContext(true);
    try {
      const { getDocumentContext } = await import('../../services/documentFlowService');
      const ctx = await getDocumentContext(documentId);
      setDocContext(ctx);
    } catch {
      setDocContext(null);
    } finally {
      setLoadingContext(false);
    }
  };

  const handleRegenerate = async () => {
    if (!docContext?.flow_slug) return;
    setRegenerateDialog(false);
    navigate(`/documents/flow/${docContext.flow_slug}`, {
      state: { fromEditor: true, formData: docContext.form_data, docName: docContext.title },
    });
  };

  const handleReuseContext = (targetSlug?: string) => {
    setReuseContextDialog(false);
    const slug = targetSlug || 'documento-livre';
    navigate(`/documents/flow/${slug}`, {
      state: { fromEditor: true, formData: docContext?.form_data || {}, docName: '' },
    });
  };

  // Handler para quando o usuário clica no botão de feedback no editor
  const handleFeedbackRequest = (selectedText: string, context: string) => {
    setFeedbackText(selectedText);
    setFeedbackContext(context);
    setShowTextFeedback(true);
  };

  // Auto-save: salvar automaticamente a cada 30 segundos
  // Mantém refs sincronizados para o salvamento ao sair (beforeunload)
  useEffect(() => { contentRef.current = content; }, [content]);
  useEffect(() => { hasUnsavedRef.current = hasUnsavedChanges; }, [hasUnsavedChanges]);

  // Salvamento com retry (backoff) e indicador de estado visível
  const persistContent = useCallback(async (): Promise<boolean> => {
    if (!documentId) return false;
    setSaving(true);
    setSaveStatus('saving');
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await api.put(`/ai-documents/document/${documentId}/update-content`, {
          content: contentRef.current,
          title: document?.title,
        });
        setHasUnsavedChanges(false);
        setLastAutoSave(new Date());
        setSaveStatus('saved');
        setSaving(false);
        return true;
      } catch (err) {
        if (attempt === maxAttempts) {
          console.error('Erro no auto-save (todas as tentativas falharam):', err);
          setSaveStatus('error');
          setSaving(false);
          return false;
        }
        await new Promise(r => setTimeout(r, 1000 * attempt)); // backoff: 1s, 2s
      }
    }
    return false;
  }, [documentId, document?.title]);

  // Salvar ao fechar/ocultar a aba — evita perder trabalho
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedRef.current && documentId) {
        // tentativa best-effort (não bloqueia), e aviso nativo
        try {
          const blob = new Blob([JSON.stringify({ content: contentRef.current, title: document?.title })], { type: 'application/json' });
          navigator.sendBeacon?.(
            `${(api.defaults as any).baseURL || ''}/ai-documents/document/${documentId}/update-content`,
            blob
          );
        } catch {}
        e.preventDefault();
        e.returnValue = '';
      }
    };
    const onVisibility = () => {
      if (document?.title !== undefined && hasUnsavedRef.current && window.document.visibilityState === 'hidden') {
        persistContent();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [documentId, document?.title, persistContent]);

  useEffect(() => {
    if (autoSaveEnabled && hasUnsavedChanges && documentId && !saving) {
      // Limpar timer anterior
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      // Configurar novo timer para 30 segundos
      autoSaveTimerRef.current = setTimeout(() => { persistContent(); }, 30000);
    }
    
    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, content, documentId, autoSaveEnabled, saving, document?.title]);

  // Atalhos de teclado
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      // Ctrl+S ou Cmd+S: Salvar
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (hasUnsavedChanges && !saving) {
          try {
            setSaving(true);
            await api.put(`/ai-documents/document/${documentId}/update-content`, {
              content: content,
              title: document?.title
            });
            setHasUnsavedChanges(false);
            setLastAutoSave(new Date());
          } catch (err) {
            console.error('Erro ao salvar:', err);
            setError('Erro ao salvar documento');
          } finally {
            setSaving(false);
          }
        }
      }
      
      // Ctrl+D ou Cmd+D: Download
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        setDownloadFormatDialog(true);
      }
      
      // Escape: Fechar dialogs
      if (e.key === 'Escape') {
        setDownloadFormatDialog(false);
        setInfoDialog(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasUnsavedChanges, saving, content, documentId, document?.title]);

  // Capturar dados do wizard
  useEffect(() => {
    const state = location.state as any;
    
    // Verificar se já processou este state para evitar duplicação
    if (generationRef.current || isGenerating) {
      return;
    }
    
    if (state?.fromWizard && state?.documentData && !state?.skipLoading) {
      // Wizard solicitando geração de documento
      generationRef.current = true;
      setDocumentData(state.documentData);
      setIsFromWizard(true);
      generateDocumentFromWizard(state.documentData);
    } else if (state?.skipLoading && state?.documentData) {
      // Caso especial: documento já foi gerado, apenas carregar no editor
      setDocument(state.documentData);
      setContent(state.documentData.content);
      setIsFromWizard(true);
      setLoading(false);
      generationRef.current = false;
    } else if (documentId === 'new' && !state?.fromWizard) {
      // Documento novo sem wizard - apenas carregar templates
      setLoading(true);
      loadTemplates();
      generationRef.current = false;
    } else if (documentId && documentId !== 'new') {
      // Documento existente - carregar documento e templates
      setLoading(true);
      loadDocument();
      loadTemplates();
      generationRef.current = false;
    } else {
      // Caso padrão
      setLoading(false);
    }
  }, [location.state, documentId]);

  // Auto-show review checklist once after document loads when coming from wizard
  useEffect(() => {
    if (!document || reviewShownRef.current) return;
    const state = location.state as any;
    if (state?.showReview && documentId && documentId !== 'new') {
      reviewShownRef.current = true;
      const rd = loadReviewData(documentId);
      if (rd) {
        setReviewData(rd);
        setShowReviewChecklist(true);
      }
    }
  }, [document]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateDocumentFromWizard = async (wizardData: any) => {
    // Prevenir múltiplas execuções simultâneas
    if (isGenerating) {
      return;
    }

    try {
      setIsGenerating(true);
      setLoading(true);
      setError(null);
      setLoadingProgress(0);
      
      // Etapa 1: Preparando dados
      setLoadingStep('Preparando dados do assistente...');
      setLoadingProgress(10);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Etapa 2: Conectando com IA
      setLoadingStep('Conectando com inteligência artificial...');
      setLoadingProgress(25);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Etapa 3: Analisando informações
      setLoadingStep('Analisando informações jurídicas...');
      setLoadingProgress(40);
      await new Promise(resolve => setTimeout(resolve, 400));
      
      // Etapa 4: Gerando documento
      setLoadingStep('Gerando documento personalizado...');
      setLoadingProgress(60);
      
      // Chamar API para gerar documento baseado nos dados do wizard
      const payload = {
        selected_areas: wizardData.selected_areas || (wizardData.area_direito ? [wizardData.area_direito] : []),
        area_direito: wizardData.area_direito,
        tipo_documento: wizardData.tipo_documento || '', // ID do tipo de documento selecionado
        nome_acao: wizardData.nome_acao,
        descricao_fatos: wizardData.descricao_fatos,
        context_text: wizardData.context_text || '',
        tribunais_selecionados: wizardData.tribunais_selecionados || [],
        topicos_selecionados: wizardData.topicos_selecionados || [],
        topicos_detalhados: wizardData.topicos_detalhados || [],
        pedidos: wizardData.pedidos || [], // Pedidos selecionados
        dados_autor: wizardData.dados_autor || {},
        dados_reu: wizardData.dados_reu || {},
        valor_causa: wizardData.valor_causa || '',
        observacoes: wizardData.observacoes || '',
        // Campos dinâmicos do formulário específico do tipo de documento
        campos_dinamicos: wizardData.campos_dinamicos || {},
        rito_processual: wizardData.rito_processual || '',
        competencia: wizardData.competencia || '',
        document_name: wizardData.document_name || wizardData.nome_acao || 'Documento Jurídico',
        folder_id: wizardData.folder_id || null,
        format_options: wizardData.format_options || {}
      };
      
      setLoadingStep('Gerando seu documento com Inteligência Artificial...');
      setLoadingProgress(65);
      
      const response = await api.post('/ai-documents/generate-from-wizard', payload);
      
      // Etapa 5: Processando resultado
      setLoadingStep('Processando resultado...');
      setLoadingProgress(80);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (response.data.status === 'success') {
        // Etapa 6: Finalizando
        setLoadingStep('Finalizando documento...');
        setLoadingProgress(95);
        await new Promise(resolve => setTimeout(resolve, 200));
        
        const generatedDoc = response.data.data;
        setDocument(generatedDoc);
        setContent(generatedDoc.content);
        
        setLoadingProgress(100);
        setLoadingStep('Documento gerado com sucesso!');
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Navegar diretamente para o editor com o documento gerado
        navigate(`/documents/editor-advanced/${generatedDoc.id}`, { 
          replace: true,
          state: { 
            fromWizard: true, 
            documentData: generatedDoc,
            skipLoading: true // Flag para pular loading no editor
          } 
        });
      } else {
        setError('Erro ao gerar documento');
      }
    } catch (err: any) {
      console.error('Erro ao gerar documento:', err);
      console.error('Erro detalhado:', err.response?.data || err.message);
      
      // Mensagem de erro mais detalhada
      const errorMessage = err.response?.data?.detail || err.message || 'Erro desconhecido ao gerar documento';
      setError(`Erro ao gerar documento: ${errorMessage}`);
    } finally {
      setLoading(false);
      setLoadingStep('');
      setLoadingProgress(0);
      setIsGenerating(false);
      // NÃO resetar generationRef aqui - ele só deve ser resetado quando carregar o documento gerado
      // ou quando navegar para outra página
    }
  };

  const loadDocument = async () => {
    try {
      setError(null);
      
      const response = await api.get(`/ai-documents/document/${documentId}/preview`);
      
      if (response.data.status === 'success') {
        const docData = response.data.data;
        setDocument(docData);
        // Citação enviada pelo "Usar em Petição" da busca de jurisprudência
        const pending = pendingCitationRef.current ?? (documentId ? takePendingCitation(documentId) : null);
        pendingCitationRef.current = pending;
        const alreadyInserted = !!pending && (docData.content || '').includes(pending.citation.replace(/&/g, '&amp;'));
        if (pending && !alreadyInserted) {
          setContent((docData.content || '') + citationToHtml(pending));
          setHasUnsavedChanges(true);
          setCitationInserted(true);
        } else {
          setContent(docData.content);
        }
        // Load review data for toolbar button (if exists from a previous wizard session)
        if (documentId && documentId !== 'new') {
          const rd = loadReviewData(documentId);
          if (rd) setReviewData(rd);
        }
      } else {
        // Só mostrar erro se não for documento novo
        if (documentId !== 'new') {
          setError('Documento não encontrado');
        }
      }
    } catch (err: any) {
      console.error('Erro ao carregar documento:', err);
      // Só mostrar erro se não for documento novo
      if (documentId !== 'new') {
        if (err.response?.status === 404) {
          setError('Documento não encontrado');
        } else {
          setError('Erro ao carregar documento');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await api.get('/ai-documents/templates');
      
      // Verificar se há dados válidos
      const data = response.data?.data || response.data || [];
      
      if (Array.isArray(data) && data.length > 0) {
        // Converter templates da API para o formato esperado
        const templatesData = data.map((t: any) => ({
          id: t.id,
          name: t.name,
          category: t.category || 'Geral',
          content: t.content,
          fields: t.validation_rules?.required_fields?.map((field: string) => ({
            name: field,
            type: 'text' as const,
            label: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            required: true
          })) || []
        }));
        setTemplates(templatesData);
      } else {
        setTemplates([]);
      }
    } catch (err: any) {
      // Não mostrar erro de templates para não interferir na experiência
      setTemplates([]);
    } finally {
      // Só desativar loading se não estiver vindo do wizard
      if (!isFromWizard) {
        setLoading(false);
      }
    }
  };

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    setHasUnsavedChanges(true);
  };

  const handleInsertJurisprudence = (jurisprudence: any) => {
    // Formatar a jurisprudência profissionalmente
    const formattedJurisprudence = `
      <div style="border-left: 4px solid #2e7d32; background-color: #f1f8e9; padding: 16px; margin: 20px 0; page-break-inside: avoid;">
        <h3 style="color: #2e7d32; margin-top: 0; font-size: 14px; font-weight: bold;">
          📋 ${jurisprudence.title}
        </h3>
        
        <div style="margin-bottom: 12px;">
          <strong style="color: #1976d2;">Tribunal:</strong> 
          <span style="background-color: #e3f2fd; padding: 2px 8px; border-radius: 4px; font-size: 12px;">
            ${jurisprudence.court_name}
          </span>
          ${jurisprudence.judgment_date ? `
            <span style="margin-left: 8px; color: #666; font-size: 12px;">
              • Julgado em ${new Date(jurisprudence.judgment_date).toLocaleDateString('pt-BR')}
            </span>
          ` : ''}
        </div>
        
        ${jurisprudence.cnj_number ? `
          <div style="margin-bottom: 8px; font-size: 12px; color: #666;">
            <strong>Número CNJ:</strong> ${jurisprudence.cnj_number}
          </div>
        ` : ''}
        
        <div style="margin-top: 12px;">
          <strong style="color: #1976d2;">Ementa:</strong>
          <p style="margin: 8px 0; text-align: justify; line-height: 1.6; font-size: 13px;">
            ${jurisprudence.ementa}
          </p>
        </div>
        
        ${jurisprudence.binding_thesis ? `
          <div style="background-color: #fff3e0; border-left: 3px solid #ff9800; padding: 12px; margin-top: 12px;">
            <strong style="color: #e65100;">⚖️ Tese Vinculante:</strong>
            <p style="margin: 8px 0; text-align: justify; line-height: 1.6; font-size: 13px;">
              ${jurisprudence.binding_thesis}
            </p>
          </div>
        ` : ''}
        
        ${jurisprudence.summary ? `
          <div style="margin-top: 12px;">
            <strong style="color: #0288d1;">💡 Resumo:</strong>
            <p style="margin: 8px 0; text-align: justify; line-height: 1.6; font-size: 13px; color: #555;">
              ${jurisprudence.summary}
            </p>
          </div>
        ` : ''}
      </div>
    `;
    
    // Insere a jurisprudência formatada no final do conteúdo
    const newContent = content + formattedJurisprudence;
    setContent(newContent);
    setHasUnsavedChanges(true);
    
    // Mostrar feedback de sucesso
    alert('✅ Jurisprudência inserida com sucesso!');
  };

  // Funções para editar título
  const handleStartEditingTitle = () => {
    setTempTitle(document?.title || '');
    setIsEditingTitle(true);
  };

  const handleCancelEditingTitle = () => {
    setIsEditingTitle(false);
    setTempTitle('');
  };

  const handleSaveTitleChange = async () => {
    if (!tempTitle.trim()) {
      handleCancelEditingTitle();
      return;
    }

    try {
      setSaving(true);
      const response = await api.put(`/ai-documents/document/${documentId}/update-content`, {
        content: content,
        title: tempTitle.trim()
      });

      if (response.data.status === 'success' && document) {
        setDocument({
          ...document,
          title: tempTitle.trim(),
          updated_at: new Date().toISOString()
        });
        setIsEditingTitle(false);
        setTempTitle('');
        
        // Disparar evento customizado para atualizar lista de documentos
        window.dispatchEvent(new CustomEvent('document-updated', { 
          detail: { 
            id: documentId, 
            title: tempTitle.trim() 
          } 
        }));
      }
    } catch (err: any) {
      console.error('Erro ao salvar título:', err);
      setError('Erro ao salvar título. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await api.put(`/ai-documents/document/${documentId}/update-content`, {
        content: content,
        title: document?.title
      });
      
      if (response.data.status === 'success') {
        setHasUnsavedChanges(false);
        
        // Atualizar documento local
        if (document) {
          setDocument({
            ...document,
            content: content,
            updated_at: new Date().toISOString()
          });
        }
      }
    } catch (err: any) {
      console.error('Erro ao salvar documento:', err);
      setError('Erro ao salvar documento');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadFormat = async (format: 'docx' | 'pdf') => {
    try {
      setDownloadFormatDialog(false);
      
      // Salvar antes de baixar se houver alterações
      if (hasUnsavedChanges) {
        await handleSave();
      }
      
      const endpoint = format === 'pdf' 
        ? `/ai-documents/document/${documentId}/download-pdf`
        : `/ai-documents/document/${documentId}/download-docx`;
      
      const response = await api.get(endpoint, {
        responseType: 'blob'
      });
      
      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${document?.title || 'documento'}.${format}`);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      trackEvent('document_downloaded', { document_id: documentId, flow_id: (document as any)?.flow_id || undefined });
      
    } catch (err: any) {
      console.error('Erro ao baixar documento:', err);
      setError('Erro ao baixar documento');
    }
  };
  
  const handleDownload = () => {
    // Abrir dialog para escolher formato
    setDownloadFormatDialog(true);
  };

  const handleValidateDocument = () => {
    // Esta função seria chamada pelo editor quando necessário
    // Por agora, apenas mostra o diálogo de informações
    setInfoDialog(true);
  };

  const reviewRightOffset = showJurisprudencePanel ? 436 : 16;
  const isReviewSideVisible = Boolean(reviewData && showReviewChecklist && reviewLayoutMode === 'side');
  const isReviewTopVisible = Boolean(reviewData && showReviewChecklist && reviewLayoutMode === 'top');

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ 
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          p: 4 
        }}
      >
        <Paper 
          elevation={8} 
          sx={{ 
            p: 6, 
            borderRadius: 4, 
            textAlign: 'center',
            minWidth: 400,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Logo Animado */}
          <Box
            sx={{
              position: 'relative',
              width: 120,
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              mx: 'auto',
            }}
          >
            {/* Anel pulsante externo */}
            <Box
              sx={{
                position: 'absolute',
                width: 120,
                height: 120,
                borderRadius: '50%',
                border: '3px solid',
                borderColor: 'primary.main',
                opacity: 0.2,
                animation: 'pulseRingOuter 2.5s ease-out infinite',
                '@keyframes pulseRingOuter': {
                  '0%': { transform: 'scale(0.8)', opacity: 0.4 },
                  '50%': { transform: 'scale(1.15)', opacity: 0.1 },
                  '100%': { transform: 'scale(0.8)', opacity: 0.4 },
                },
              }}
            />
            {/* Anel pulsante interno */}
            <Box
              sx={{
                position: 'absolute',
                width: 100,
                height: 100,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: 'primary.light',
                opacity: 0.3,
                animation: 'pulseRingInner 2s ease-out infinite 0.3s',
                '@keyframes pulseRingInner': {
                  '0%': { transform: 'scale(0.9)', opacity: 0.5 },
                  '50%': { transform: 'scale(1.1)', opacity: 0.2 },
                  '100%': { transform: 'scale(0.9)', opacity: 0.5 },
                },
              }}
            />
            {/* Logo flutuante */}
            <Box
              component="img"
              src="/favicon.svg"
              alt="Logo Advogada Parceira"
              sx={{
                width: 70,
                height: 70,
                animation: 'logoFloatDoc 2.5s ease-in-out infinite',
                filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))',
                '@keyframes logoFloatDoc': {
                  '0%': { transform: 'translateY(0px) scale(1)' },
                  '50%': { transform: 'translateY(-10px) scale(1.05)' },
                  '100%': { transform: 'translateY(0px) scale(1)' },
                },
              }}
            />
          </Box>
          
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
            {isFromWizard ? 'Gerando documento com IA...' : 'Carregando documento...'}
          </Typography>
          
          {loadingStep && (
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              {loadingStep}
            </Typography>
          )}
          
          {loadingProgress > 0 && (
            <Box sx={{ mb: 3, width: '100%' }}>
              <LinearProgress 
                variant="determinate" 
                value={loadingProgress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                  }
                }} 
              />
              <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                {loadingProgress}% completo
              </Typography>
            </Box>
          )}
          
          <Typography 
            variant="caption" 
            sx={{ 
              mt: 2, 
              color: 'text.secondary',
              fontStyle: 'italic'
            }}
          >
            {isFromWizard ? 'Advogada Parceira IA' : 'Aguarde um momento'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/documents')}
          startIcon={<ArrowBackIcon />}
        >
          Voltar para Documentos
        </Button>
      </Container>
    );
  }

  if (!document) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">Documento não encontrado</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <AppBar position="static" color="default" elevation={0} sx={{ 
        backgroundColor: 'white',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Toolbar variant="dense" sx={{ minHeight: 48, gap: 1 }}>
          {/* Left: Back + Title */}
          <IconButton onClick={() => navigate('/documents')} size="small" sx={{ mr: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {isEditingTitle ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField value={tempTitle} onChange={(e) => setTempTitle(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleSaveTitleChange(); }}
                  onKeyDown={(e) => { if (e.key === 'Escape') handleCancelEditingTitle(); }}
                  variant="standard" size="small" autoFocus
                  sx={{ flexGrow: 1, maxWidth: 400, '& input': { fontSize: '0.9rem', fontWeight: 600 } }}
                />
                <Button size="small" variant="contained" onClick={handleSaveTitleChange} disabled={saving || !tempTitle.trim()} sx={{ textTransform: 'none', minWidth: 'auto', px: 1.5, py: 0.3, fontSize: '0.75rem' }}>OK</Button>
                <Button size="small" onClick={handleCancelEditingTitle} sx={{ textTransform: 'none', minWidth: 'auto', px: 1, py: 0.3, fontSize: '0.75rem' }}>Cancelar</Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" component="div" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 350, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                  title="Clique para editar" onClick={handleStartEditingTitle}>
                  {document.title}
                </Typography>
                <IconButton size="small" onClick={handleStartEditingTitle} sx={{ p: '2px', color: 'text.disabled' }}>
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Chip label={document.document_type || 'Documento'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', ml: 0.5 }} />
                {hasUnsavedChanges && <Chip label="Editado" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
              </Box>
            )}
          </Box>

          {/* Center/Right: Actions */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            {/* Primary actions - always visible */}
            <Button size="small" variant={hasUnsavedChanges ? 'contained' : 'text'} color={hasUnsavedChanges ? 'primary' : 'inherit'}
              startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon sx={{ fontSize: 16 }} />}
              onClick={handleSave} disabled={saving || !hasUnsavedChanges}
              sx={{ textTransform: 'none', fontSize: '0.8rem', minWidth: 'auto', px: 1.5, py: 0.4, borderRadius: 1.5, color: hasUnsavedChanges ? undefined : '#5f6368' }}>
              {saving ? 'Salvando' : 'Salvar'}
            </Button>

            <Button size="small" variant="text" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />} onClick={handleDownload}
              sx={{ textTransform: 'none', fontSize: '0.8rem', color: '#5f6368', minWidth: 'auto', px: 1.5, py: 0.4 }}>
              Baixar
            </Button>

            <Button size="small" variant={showJurisprudencePanel ? 'contained' : 'text'} color={showJurisprudencePanel ? 'primary' : 'inherit'}
              startIcon={<GavelIcon sx={{ fontSize: 16 }} />} onClick={() => setShowJurisprudencePanel(!showJurisprudencePanel)}
              sx={{ textTransform: 'none', fontSize: '0.8rem', minWidth: 'auto', px: 1.5, py: 0.4, color: showJurisprudencePanel ? undefined : '#5f6368' }}>
              Juris.
            </Button>

            {reviewData && (
              <Button
                size="small"
                variant={showReviewChecklist ? 'contained' : 'text'}
                color={showReviewChecklist ? 'success' : 'inherit'}
                startIcon={<AssignmentTurnedInIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  setReviewLayoutMode('side');
                  setShowReviewChecklist(true);
                }}
                sx={{ textTransform: 'none', fontSize: '0.8rem', minWidth: 'auto', px: 1.5, py: 0.4, color: showReviewChecklist ? undefined : '#2e7d32' }}
              >
                Revisão
              </Button>
            )}

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 24, alignSelf: 'center' }} />

            {/* Secondary actions - collapsed into "More" menu */}
            <IconButton size="small" onClick={(e) => setMoreMenuAnchor(e.currentTarget)} sx={{ color: '#5f6368' }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>

            <Menu anchorEl={moreMenuAnchor} open={Boolean(moreMenuAnchor)} onClose={() => setMoreMenuAnchor(null)}
              PaperProps={{ sx: { minWidth: 200, mt: 0.5 } }}>
              <MenuItem onClick={() => { setMoreMenuAnchor(null); handleLoadDocContext(); setRegenerateDialog(true); }}>
                <ReplayIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} /> Regenerar documento
              </MenuItem>
              <MenuItem onClick={() => { setMoreMenuAnchor(null); handleLoadDocContext(); setReuseContextDialog(true); }}>
                <ContentCopyIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} /> Reutilizar contexto
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { setMoreMenuAnchor(null); handleValidateDocument(); }}>
                <InfoIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} /> Informações e validação
              </MenuItem>
              <MenuItem onClick={() => {
                setMoreMenuAnchor(null);
                if (documentId && documentId !== 'new') {
                  const shareUrl = `${window.location.origin}/documents/editor-advanced/${documentId}`;
                  navigator.clipboard.writeText(shareUrl);
                  setError(null);
                }
              }}>
                <ShareIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} /> Copiar link do documento
              </MenuItem>
            </Menu>

            {/* Auto-save indicator com estado visível */}
            {saveStatus === 'saving' && (
              <Chip size="small" label="Salvando…" color="info" variant="outlined"
                sx={{ height: 22, fontSize: '0.65rem' }} />
            )}
            {saveStatus === 'error' && (
              <Tooltip title="Falha ao salvar. Tentaremos de novo; clique em Salvar para forçar.">
                <Chip size="small" label="Erro ao salvar" color="error"
                  onClick={() => persistContent()}
                  sx={{ height: 22, fontSize: '0.65rem', cursor: 'pointer' }} />
              </Tooltip>
            )}
            {saveStatus !== 'saving' && saveStatus !== 'error' && hasUnsavedChanges && (
              <Chip size="small" label="Não salvo" color="warning" variant="outlined"
                sx={{ height: 22, fontSize: '0.65rem' }} />
            )}
            {saveStatus === 'saved' && !hasUnsavedChanges && lastAutoSave && (
              <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                label={`Salvo ${lastAutoSave.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
                color="success" variant="outlined"
                sx={{ height: 22, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12 } }}
              />
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Editor */}
      <Box
        ref={editorContainerRef}
        sx={{
          flex: 1,
          overflow: 'hidden',
          mr: isReviewSideVisible ? { xs: 0, xl: '396px' } : 0,
          mt: isReviewTopVisible ? 'calc(42vh + 16px)' : 0,
          transition: 'margin 0.22s ease, transform 0.22s ease',
        }}
      >
        <SimpleLegalEditor
          content={content}
          onContentChange={handleContentChange}
          onSave={handleSave}
          documentId={documentId}
          onFeedbackRequest={handleFeedbackRequest}
        />
      </Box>

      {/* Compact status strip - only shows when relevant */}
      {(hasUnsavedChanges || !validationResults.isValid) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 0.4, borderTop: '1px solid', borderColor: 'divider', backgroundColor: '#fafafa', minHeight: 24 }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
            Modificado: {new Date(document.updated_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </Typography>
          {hasUnsavedChanges && <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>Alterações não salvas</Typography>}
          {!validationResults.isValid && <Typography variant="caption" color="error.main" sx={{ fontSize: '0.65rem' }}>{validationResults.errors.length} erros</Typography>}
        </Box>
      )}

      {/* Painel de Jurisprudência Integrado */}
      <IntegratedJurisprudencePanel
        documentContent={content}
        documentTitle={document?.title || ''}
        isVisible={showJurisprudencePanel}
        onClose={() => setShowJurisprudencePanel(false)}
        onInsertJurisprudence={handleInsertJurisprudence}
        selectedTribunals={documentData?.tribunais_selecionados || []}
        documentArea={documentData?.area_direito || ''}
      />

      {/* Lista de Revisão */}
      {reviewData && documentId && documentId !== 'new' && (
        <ReviewChecklist
          open={showReviewChecklist}
          docId={documentId}
          reviewData={reviewData}
          documentContent={content}
          mode={reviewLayoutMode}
          rightOffset={reviewRightOffset}
          onModeChange={setReviewLayoutMode}
          onClose={() => {
            setShowReviewChecklist(false);
            setReviewLayoutMode('side');
          }}
        />
      )}

      {/* Dialog de Informações */}
      <Dialog 
        open={infoDialog} 
        onClose={() => setInfoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Informações do Documento</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Detalhes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Tipo:</strong> {document.document_type || 'Documento Jurídico'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Criado em:</strong> {new Date(document.created_at).toLocaleString('pt-BR')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Modificado em:</strong> {new Date(document.updated_at).toLocaleString('pt-BR')}
              </Typography>
            </Box>
            
            <Divider />
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Validação
              </Typography>
              {validationResults.isValid ? (
                <Alert severity="success">
                  Documento válido - todos os campos obrigatórios estão preenchidos
                </Alert>
              ) : (
                <Alert severity="warning">
                  <Typography variant="body2" gutterBottom>
                    Campos obrigatórios não preenchidos:
                  </Typography>
                  <ul>
                    {validationResults.errors.map((error, index) => (
                      <li key={index}>
                        <Typography variant="body2">{error}</Typography>
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}
            </Box>
            
            <Divider />
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Estatísticas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                📄 Caracteres: {content.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                📝 Palavras: {content.split(/\s+/).filter(word => word.length > 0).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                📖 Parágrafos: {content.split('\n\n').filter(p => p.trim().length > 0).length}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialog(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para escolher formato de download */}
      <Dialog 
        open={downloadFormatDialog} 
        onClose={() => setDownloadFormatDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Escolha o formato do arquivo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Selecione o formato em que deseja baixar o documento:
          </Typography>
          <Stack spacing={2}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleDownloadFormat('docx')}
              startIcon={<DownloadIcon />}
              fullWidth
              sx={{ 
                justifyContent: 'flex-start',
                p: 2,
                borderColor: '#1E4562',
                color: '#1E4562',
                '&:hover': {
                  borderColor: '#0F2A44',
                  bgcolor: 'rgba(30, 69, 98, 0.05)'
                }
              }}
            >
              <Box sx={{ textAlign: 'left', flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Word (.docx)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Editável no Microsoft Word ou Google Docs
                </Typography>
              </Box>
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleDownloadFormat('pdf')}
              startIcon={<DownloadIcon />}
              fullWidth
              sx={{ 
                justifyContent: 'flex-start',
                p: 2,
                borderColor: '#1E4562',
                color: '#1E4562',
                '&:hover': {
                  borderColor: '#0F2A44',
                  bgcolor: 'rgba(30, 69, 98, 0.05)'
                }
              }}
            >
              <Box sx={{ textAlign: 'left', flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  PDF (.pdf)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Formato universal para visualização e impressão
                </Typography>
              </Box>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadFormatDialog(false)}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={citationInserted}
        autoHideDuration={8000}
        onClose={() => setCitationInserted(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setCitationInserted(false)}>
          Citação inserida no final da peça. Mova-a para a fundamentação e cole o trecho da decisão no campo [PREENCHER].
        </Alert>
      </Snackbar>

      {/* Widget de Feedback - Canto inferior esquerdo */}
      <FeedbackWidget
        feature="document_editor"
        entityType="document"
        entityId={documentId}
        position="bottom-left"
        title="Avalie o documento"
        compact={true}
        onFeedbackSubmitted={(rating, comment) => {
          console.log('Feedback enviado:', { rating, comment, documentId });
        }}
      />

      {/* Modal de feedback para texto selecionado */}
      {showTextFeedback && feedbackText && (
        <>
          {/* Overlay escuro */}
          <Box
            onClick={() => {
              setShowTextFeedback(false);
              setFeedbackText('');
              setFeedbackContext('');
            }}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1200,
            }}
          />
          
          {/* Widget de feedback centralizado */}
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1300,
            }}
          >
            <FeedbackWidget
              feature="document_editor"
              entityType="document"
              entityId={documentId}
              position="inline"
              title="Avalie este texto"
              compact={false}
              open={true}
              selectedText={feedbackText}
              textContext={feedbackContext}
              onFeedbackSubmitted={() => {
                setShowTextFeedback(false);
                setFeedbackText('');
                setFeedbackContext('');
              }}
              onClose={() => {
                setShowTextFeedback(false);
                setFeedbackText('');
                setFeedbackContext('');
              }}
            />
          </Box>
        </>
      )}
      {/* Regenerate Dialog */}
      <Dialog open={regenerateDialog} onClose={() => setRegenerateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Regenerar Documento</DialogTitle>
        <DialogContent>
          {loadingContext ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : docContext?.flow_slug ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Você será redirecionado para o fluxo <strong>{docContext.flow_slug}</strong> com todos os dados preenchidos.
                Você poderá ajustar os campos antes de gerar novamente.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                O documento atual será mantido. Um novo documento será gerado com os mesmos dados (ou ajustados por você).
              </Typography>
            </>
          ) : (
            <Alert severity="warning">
              Este documento não possui um fluxo configurável associado. Não é possível regenerar.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleRegenerate} disabled={!docContext?.flow_slug}>
            Ir para o Fluxo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reuse Context Dialog */}
      <Dialog open={reuseContextDialog} onClose={() => setReuseContextDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reutilizar Contexto</DialogTitle>
        <DialogContent>
          {loadingContext ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                O contexto deste documento será exportado para um novo fluxo. A IA organizará os dados para o novo tipo de documento.
              </Alert>
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                <Button variant="outlined" fullWidth onClick={() => handleReuseContext('documento-livre')}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5, borderRadius: 2 }}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="subtitle2" fontWeight={700}>Documento Livre (IA decide)</Typography>
                    <Typography variant="caption" color="text.secondary">A IA analisa o contexto e cria o melhor documento</Typography>
                  </Box>
                </Button>
                {docContext?.flow_slug && docContext.flow_slug !== 'documento-livre' && (
                  <Button variant="outlined" fullWidth onClick={() => handleReuseContext(docContext.flow_slug)}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5, borderRadius: 2 }}>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="subtitle2" fontWeight={700}>Mesmo fluxo ({docContext.flow_slug})</Typography>
                      <Typography variant="caption" color="text.secondary">Reutilizar no mesmo tipo de documento</Typography>
                    </Box>
                  </Button>
                )}
                <Button variant="outlined" fullWidth onClick={() => { setReuseContextDialog(false); navigate('/documents/flows'); }}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5, borderRadius: 2 }}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="subtitle2" fontWeight={700}>Escolher outro fluxo</Typography>
                    <Typography variant="caption" color="text.secondary">Selecionar manualmente um fluxo diferente</Typography>
                  </Box>
                </Button>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReuseContextDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewDocumentEditorPage; 